/**
 * MediaPipe Pose Detection Utilities
 *
 * Provides a singleton manager for MediaPipe Pose Landmarker
 * to detect infant poses during video recording.
 */

import { PoseLandmarker, FilesetResolver, PoseLandmarkerResult } from "@mediapipe/tasks-vision";
import { debugLog } from '../common/debugUtilities';

export interface PoseDetectionConfig {
  modelAssetPath?: string;
  minPoseDetectionConfidence?: number;
  minPosePresenceConfidence?: number;
  minTrackingConfidence?: number;
  numPoses?: number;
}

const DEFAULT_CONFIG: PoseDetectionConfig = {
  modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task',
  minPoseDetectionConfidence: 0.5,
  minPosePresenceConfidence: 0.5,
  minTrackingConfidence: 0.5,
  numPoses: 1
};

/**
 * Singleton manager for MediaPipe Pose Landmarker
 */
class MediaPipeManager {
  private static instance: MediaPipeManager | null = null;
  private poseLandmarker: PoseLandmarker | null = null;
  private initialized: boolean = false;
  private initializationPromise: Promise<void> | null = null;

  private constructor() {}

  /**
   * Get the singleton instance and ensure it's initialized
   */
  static async getInstance(): Promise<MediaPipeManager> {
    if (!MediaPipeManager.instance) {
      MediaPipeManager.instance = new MediaPipeManager();
    }

    if (!MediaPipeManager.instance.initialized && !MediaPipeManager.instance.initializationPromise) {
      MediaPipeManager.instance.initializationPromise = MediaPipeManager.instance.initialize();
    }

    if (MediaPipeManager.instance.initializationPromise) {
      await MediaPipeManager.instance.initializationPromise;
    }

    return MediaPipeManager.instance;
  }

  /**
   * Initialize MediaPipe Pose Landmarker
   */
  private async initialize(config: PoseDetectionConfig = DEFAULT_CONFIG): Promise<void> {
    try {
      debugLog('[MediaPipe] Initializing Pose Landmarker...');

      // Load MediaPipe Vision Tasks WASM files
      const vision = await FilesetResolver.forVisionTasks(
        "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.8/wasm"
      );

      // Create Pose Landmarker
      this.poseLandmarker = await PoseLandmarker.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath: config.modelAssetPath || DEFAULT_CONFIG.modelAssetPath,
          delegate: "GPU" // Use GPU acceleration
        },
        runningMode: "VIDEO", // For video streams
        numPoses: config.numPoses || DEFAULT_CONFIG.numPoses,
        minPoseDetectionConfidence: config.minPoseDetectionConfidence || DEFAULT_CONFIG.minPoseDetectionConfidence,
        minPosePresenceConfidence: config.minPosePresenceConfidence || DEFAULT_CONFIG.minPosePresenceConfidence,
        minTrackingConfidence: config.minTrackingConfidence || DEFAULT_CONFIG.minTrackingConfidence
      });

      this.initialized = true;
      this.initializationPromise = null;
      debugLog('[MediaPipe] Pose Landmarker initialized successfully');
    } catch (error) {
      this.initialized = false;
      this.initializationPromise = null;
      console.error('[MediaPipe] Initialization failed:', error);
      throw new Error(`Failed to initialize MediaPipe: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Detect pose in a video frame
   * @param videoElement HTML video element containing the frame
   * @param timestamp Timestamp in milliseconds
   * @returns Pose detection result or null if no pose detected
   */
  async detectPose(videoElement: HTMLVideoElement, timestamp: number): Promise<PoseLandmarkerResult | null> {
    if (!this.poseLandmarker || !this.initialized) {
      throw new Error('MediaPipe not initialized. Call getInstance() first.');
    }

    if (!videoElement.videoWidth || !videoElement.videoHeight) {
      console.warn('[MediaPipe] Video element not ready');
      return null;
    }

    try {
      const result = this.poseLandmarker.detectForVideo(videoElement, timestamp);
      return result;
    } catch (error) {
      console.error('[MediaPipe] Pose detection failed:', error);
      return null;
    }
  }

  /**
   * Check if MediaPipe is initialized and ready
   */
  isReady(): boolean {
    return this.initialized && this.poseLandmarker !== null;
  }

  /**
   * Dispose of MediaPipe resources
   */
  dispose(): void {
    if (this.poseLandmarker) {
      try {
        this.poseLandmarker.close();
        debugLog('[MediaPipe] Pose Landmarker disposed');
      } catch (error) {
        console.error('[MediaPipe] Error disposing Pose Landmarker:', error);
      }
      this.poseLandmarker = null;
    }
    this.initialized = false;
    this.initializationPromise = null;
  }

  /**
   * Reset the singleton instance (useful for testing)
   */
  static reset(): void {
    if (MediaPipeManager.instance) {
      MediaPipeManager.instance.dispose();
      MediaPipeManager.instance = null;
    }
  }
}

/**
 * Check if MediaPipe is supported in the current browser
 */
export async function checkMediaPipeSupport(): Promise<{ supported: boolean; reason?: string }> {
  try {
    // Check WebGL support (required for MediaPipe)
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');

    if (!gl) {
      return { supported: false, reason: 'WebGL not supported' };
    }

    // Check if we can initialize MediaPipe
    try {
      await MediaPipeManager.getInstance();
      return { supported: true };
    } catch (error) {
      return {
        supported: false,
        reason: error instanceof Error ? error.message : 'Unknown error during initialization'
      };
    }
  } catch (error) {
    return {
      supported: false,
      reason: 'Failed to check MediaPipe support'
    };
  }
}

/**
 * Get optimal detection interval based on device capabilities
 * Lower intervals = more frequent detection = better tracking but more CPU usage
 */
export function getOptimalDetectionInterval(): number {
  const ua = navigator.userAgent.toLowerCase();
  const memory = (navigator as any).deviceMemory;
  const cores = navigator.hardwareConcurrency || 2;

  // iOS detection
  const isOlderiPhone = /iphone (6|7|8|se|x)/.test(ua);
  const isVeryOldPhone = /iphone (4|5)/.test(ua);

  // Android detection
  const isAndroid = /android/.test(ua);
  const androidVersion = ua.match(/android (\d+)/)?.[1];
  const androidVersionNum = androidVersion ? parseInt(androidVersion) : 0;
  const isVeryOldAndroid = isAndroid && androidVersionNum > 0 && androidVersionNum < 8;
  const isOldAndroid = isAndroid && androidVersionNum > 0 && androidVersionNum < 9;
  const isBudgetAndroid = isAndroid && (
    /samsung.*(galaxy (j|a|m)\d+|sm-[ajm]\d+)/.test(ua) ||
    /moto.*(e|g)\d+/.test(ua) ||
    /redmi|xiaomi/.test(ua) ||
    /huawei.*(lite|y\d+)/.test(ua)
  );

  // Very old devices: 500ms (2 FPS) - prevent freezing
  if (isVeryOldPhone || isVeryOldAndroid || (memory && memory < 2) || cores < 2) {
    debugLog('[MediaPipe] Using 500ms interval (2 FPS) for very old device');
    return 500;
  }

  // Older/budget devices: 400ms (2.5 FPS) - balanced
  if (isOlderiPhone || isOldAndroid || isBudgetAndroid || (memory && memory < 4) || cores < 4) {
    debugLog('[MediaPipe] Using 400ms interval (2.5 FPS) for older/budget device');
    return 400;
  }

  // Modern devices: 250ms (4 FPS) - smooth tracking
  debugLog('[MediaPipe] Using 250ms interval (4 FPS) for modern device');
  return 250;
}

export default MediaPipeManager;
